import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import moment from "moment";

const cellCenterSx = { display: "flex", alignItems: "center", height: "100%", width: "100%", minWidth: 0 };

function isEmptyLogDetails(details) {
    if (details == null) return true;
    if (typeof details !== "object") return false;
    const keys = Object.keys(details);
    if (keys.length === 0) return true;
    return keys.length === 1 && keys[0] === "info" && details.info === "No details";
}

function formatLogDetails(details) {
    if (isEmptyLogDetails(details)) return "";
    if (typeof details === "string") return details;
    try {
        return JSON.stringify(details);
    } catch {
        return "";
    }
}

function formatLogDetailsPretty(details) {
    if (isEmptyLogDetails(details)) return "";
    if (typeof details === "string") return details;
    try {
        return JSON.stringify(details, null, 2);
    } catch {
        return "";
    }
}

const levelConfig = {
    INFO: { color: "info", icon: InfoIcon, label: "Info" },
    WARN: { color: "warning", icon: WarningIcon, label: "Warn" },
    ERROR: { color: "error", icon: ErrorIcon, label: "Error" },
    SYSTEM: { color: "default", icon: InfoIcon, label: "System" }
};

export function getLogsColumns() {
    return [
        {
            field: "level",
            headerName: "Level",
            width: 110,
            renderCell: (params) => {
                const config = levelConfig[params.value] || levelConfig.INFO;
                const Icon = config.icon;
                return (
                    <Chip
                        size="small"
                        icon={<Icon sx={{ fontSize: 16 }} />}
                        label={config.label.toUpperCase()}
                        color={config.color}
                        variant="outlined"
                    />
                );
            }
        },
        { field: "source", headerName: "Source", flex: 0.5, minWidth: 120 },
        {
            field: "message",
            headerName: "Message",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const text = params.value ?? "";
                return (
                    <Box sx={cellCenterSx}>
                        <Tooltip title={text || ""} enterDelay={400}>
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    minWidth: 0,
                                }}
                            >
                                {text}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: "details",
            headerName: "Details",
            flex: 1,
            minWidth: 220,
            sortable: false,
            renderCell: (params) => {
                const text = formatLogDetails(params.row?.details);
                const pretty = formatLogDetailsPretty(params.row?.details);
                return (
                    <Box sx={cellCenterSx}>
                        <Tooltip
                            title={
                                pretty ? (
                                    <Typography
                                        component="pre"
                                        variant="caption"
                                        sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                    >
                                        {pretty}
                                    </Typography>
                                ) : (
                                    ""
                                )
                            }
                            enterDelay={400}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    minWidth: 0,
                                    fontFamily: pretty ? "ui-monospace, Menlo, Consolas, monospace" : "inherit",
                                    fontSize: pretty ? "0.75rem" : undefined,
                                }}
                            >
                                {text}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: "createdAt",
            headerName: "Created At",
            flex: 0.5,
            minWidth: 165,
            valueFormatter: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "")
        }
    ];
}
